/**
 * localStorage-backed ConversationStore.
 *
 * Ports the key / TTL / version-tolerance logic from useAgentChat, but stores
 * AI SDK `UIMessage[]` instead of the legacy `ChatMessage[]`. STORAGE_VERSION
 * is bumped to 8 so legacy-shaped sessions are version-rejected and silently
 * dropped — no migration code needed (clean cutover).
 */

import type { UIMessage } from 'ai';

import { sanitizeDraft, sanitizeUiText } from '../engine/sanitize';

import type { ConversationStore, PersistedConversation } from './ConversationStore';

import type { AgentContext } from '@/shared/types/chat';

const STORAGE_VERSION = 8;
const STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function keyFor(context: AgentContext): string {
  if (context.assistantType === 'global') {
    return 'ai:global';
  }
  const focusResourceId = context.focus?.resourceId ?? 'none';
  return `ai:course:${context.courseId ?? 'missing'}:resource:${focusResourceId}`;
}

/** Strip UUIDs from any text part of a persisted message before it re-enters the UI. */
function sanitizeMessage(message: UIMessage): UIMessage {
  return {
    ...message,
    parts: message.parts.map((part) =>
      part.type === 'text' ? { ...part, text: sanitizeUiText(part.text) } : part
    ),
  };
}

export const localStorageConversationStore: ConversationStore = {
  keyFor,

  load(key) {
    if (typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) return null;

      const parsed = JSON.parse(raw) as Partial<PersistedConversation>;
      if (!parsed || parsed.version !== STORAGE_VERSION || typeof parsed.updatedAt !== 'number') {
        window.localStorage.removeItem(key);
        return null;
      }

      if (Date.now() - parsed.updatedAt > STORAGE_TTL_MS) {
        window.localStorage.removeItem(key);
        return null;
      }

      const messages = Array.isArray(parsed.messages) ? parsed.messages.map(sanitizeMessage) : [];

      const activeDraft = parsed.activeDraft ? sanitizeDraft(parsed.activeDraft) : null;

      const activeDraftMessageId =
        typeof parsed.activeDraftMessageId === 'string' ? parsed.activeDraftMessageId : null;

      return {
        version: STORAGE_VERSION,
        updatedAt: parsed.updatedAt,
        messages,
        activeDraft,
        activeDraftMessageId:
          activeDraftMessageId && messages.some((m) => m.id === activeDraftMessageId)
            ? activeDraftMessageId
            : null,
      };
    } catch {
      return null;
    }
  },

  save(key, value) {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify({ ...value, version: STORAGE_VERSION }));
    } catch {
      // Best-effort persistence only.
    }
  },

  clear(key) {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Best-effort persistence only.
    }
  },
};
